import { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { Search, Plus, Edit2, ArrowRight, Package, Building2, Warehouse, Filter, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function StockPage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    
    // Data
    const [stock, setStock] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [bodegas, setBodegas] = useState([]);
    const [bodegasDestino, setBodegasDestino] = useState([]);
    const [summary, setSummary] = useState([]);
    
    // Filters
    const [filters, setFilters] = useState({
        sucursal_id: '',
        bodega_id: '',
        search: ''
    });
    
    // Modals
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    
    // Form data
    const [updateData, setUpdateData] = useState({
        producto_id: '',
        sucursal_id: '',
        bodega_id: '',
        cantidad: 0,
        motivo: ''
    });
    
    const [transferData, setTransferData] = useState({
        producto_id: '',
        sucursal_origen: '',
        bodega_origen: '',
        sucursal_destino: '',
        bodega_destino: '',
        cantidad: 0,
        motivo: ''
    });

    useEffect(() => {
        fetchSucursales();
        fetchSummary();
    }, []);

    useEffect(() => {
        if (filters.sucursal_id) {
            fetchBodegas(filters.sucursal_id);
        } else {
            setBodegas([]);
        }
    }, [filters.sucursal_id]);

    useEffect(() => {
        if (transferData.sucursal_destino) {
            fetchBodegasForTransfer(transferData.sucursal_destino);
        } else {
            setBodegasDestino([]);
        }
    }, [transferData.sucursal_destino]);

    useEffect(() => {
        fetchStock();
    }, [filters]);

    const fetchSucursales = async () => {
        try {
            const res = await fetch(`${API_URL}/api/sucursales`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSucursales(data.filter(s => s.estado !== false));
            }
        } catch (err) {
            console.error('Error fetching sucursales:', err);
        }
    };

    const fetchBodegas = async (sucursalId) => {
        try {
            const res = await fetch(`${API_URL}/api/bodegas/sucursal/${sucursalId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setBodegas(await res.json());
            }
        } catch (err) {
            console.error('Error fetching bodegas:', err);
        }
    };

    const fetchBodegasForTransfer = async (sucursalId) => {
        try {
            const res = await fetch(`${API_URL}/api/bodegas/sucursal/${sucursalId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setBodegasDestino(await res.json());
            }
        } catch (err) {
            console.error('Error fetching bodegas for transfer:', err);
        }
    };

    const fetchStock = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.sucursal_id) params.append('sucursal_id', filters.sucursal_id);
            if (filters.bodega_id) params.append('bodega_id', filters.bodega_id);
            if (filters.search) params.append('search', filters.search);

            const res = await fetch(`${API_URL}/api/stock?${params}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setStock(await res.json());
            }
        } catch (err) {
            console.error('Error fetching stock:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async () => {
        try {
            const params = filters.sucursal_id ? `?sucursal_id=${filters.sucursal_id}` : '';
            const res = await fetch(`${API_URL}/api/stock/summary${params}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                setSummary(await res.json());
            }
        } catch (err) {
            console.error('Error fetching summary:', err);
        }
    };

    const handleUpdateStock = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_URL}/api/stock`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(updateData)
            });

            if (res.ok) {
                alert('Stock actualizado correctamente');
                setShowUpdateModal(false);
                setUpdateData({ producto_id: '', sucursal_id: '', bodega_id: '', cantidad: 0, motivo: '' });
                fetchStock();
                fetchSummary();
            } else {
                const error = await res.json();
                alert(`Error: ${error.error}`);
            }
        } catch (err) {
            console.error('Error updating stock:', err);
            alert('Error al actualizar stock');
        }
    };

    const handleTransferStock = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_URL}/api/stock/transfer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(transferData)
            });

            if (res.ok) {
                alert('Transferencia realizada correctamente');
                setShowTransferModal(false);
                setTransferData({
                    producto_id: '',
                    sucursal_origen: '',
                    bodega_origen: '',
                    sucursal_destino: '',
                    bodega_destino: '',
                    cantidad: 0,
                    motivo: ''
                });
                fetchStock();
                fetchSummary();
            } else {
                const error = await res.json();
                alert(`Error: ${error.error}`);
            }
        } catch (err) {
            console.error('Error transferring stock:', err);
            alert('Error al transferir stock');
        }
    };

    const openUpdateModal = (product) => {
        setSelectedProduct(product);
        setUpdateData({
            producto_id: product.producto_id,
            sucursal_id: product.sucursal_id,
            bodega_id: product.bodega_id,
            cantidad: product.cantidad,
            motivo: ''
        });
        setShowUpdateModal(true);
    };

    const openTransferModal = (product) => {
        setSelectedProduct(product);
        setTransferData({
            producto_id: product.producto_id,
            sucursal_origen: product.sucursal_id,
            bodega_origen: product.bodega_id,
            sucursal_destino: '',
            bodega_destino: '',
            cantidad: 0,
            motivo: ''
        });
        setShowTransferModal(true);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value || 0);
    };

    // Group stock by sucursal/bodega
    const groupedStock = stock.reduce((acc, item) => {
        const key = `${item.sucursal_id}_${item.bodega_id}`;
        if (!acc[key]) {
            acc[key] = {
                sucursal_id: item.sucursal_id,
                sucursal_nombre: item.sucursal_nombre,
                bodega_id: item.bodega_id,
                bodega_nombre: item.bodega_nombre,
                items: []
            };
        }
        acc[key].items.push(item);
        return acc;
    }, {});

    return (
        <div className="page stock-page">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Package size={28} /> Gestión de Stock
                    </h1>
                    <p className="text-gray-500">Administre el stock por sucursal y bodega</p>
                </div>
                <button
                    onClick={() => { fetchStock(); fetchSummary(); }}
                    className="btn-secondary flex items-center gap-2"
                >
                    <RefreshCw size={18} /> Actualizar
                </button>
            </div>

            {/* Summary Cards */}
            {summary.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {summary.map((sum) => (
                        <div key={`${sum.sucursal_id}_${sum.bodega_id}`} className="bg-white p-4 rounded-lg shadow border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Building2 size={16} className="text-indigo-500" />
                                    <span className="font-semibold text-sm">{sum.sucursal_nombre}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <Warehouse size={14} className="text-gray-400" />
                                <span className="text-xs text-gray-500">{sum.bodega_nombre}</span>
                            </div>
                            <div className="mt-3 space-y-1">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Productos:</span>
                                    <span className="font-bold">{sum.productos_count}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Stock Total:</span>
                                    <span className="font-bold text-indigo-600">{sum.stock_total}</span>
                                </div>
                                {sum.productos_sin_stock > 0 && (
                                    <div className="flex justify-between text-xs mt-2 pt-2 border-t">
                                        <span className="text-red-600">Sin Stock:</span>
                                        <span className="font-bold text-red-600">{sum.productos_sin_stock}</span>
                                    </div>
                                )}
                                {sum.productos_bajo_minimo > 0 && (
                                    <div className="flex justify-between text-xs">
                                        <span className="text-orange-600">Bajo Mínimo:</span>
                                        <span className="font-bold text-orange-600">{sum.productos_bajo_minimo}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <Filter size={20} className="text-gray-500" />
                    <h3 className="font-semibold">Filtros</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Sucursal</label>
                        <select
                            className="w-full p-2 border rounded"
                            value={filters.sucursal_id}
                            onChange={(e) => {
                                setFilters({ ...filters, sucursal_id: e.target.value, bodega_id: '' });
                            }}
                        >
                            <option value="">Todas las sucursales</option>
                            {sucursales.map(s => (
                                <option key={s.id || s.sucursal_id} value={s.id || s.sucursal_id}>
                                    {s.nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Bodega</label>
                        <select
                            className="w-full p-2 border rounded"
                            value={filters.bodega_id}
                            onChange={(e) => setFilters({ ...filters, bodega_id: e.target.value })}
                            disabled={!filters.sucursal_id}
                        >
                            <option value="">Todas las bodegas</option>
                            {bodegas.map(b => (
                                <option key={b.id || b.bodega_id} value={b.id || b.bodega_id}>
                                    {b.nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Buscar Producto</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                            <input
                                type="text"
                                className="w-full pl-14 pr-4 py-2 border rounded"
                                placeholder="Nombre o código de barra..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Stock Table */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Cargando stock...</div>
                ) : stock.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <Package size={48} className="mx-auto mb-4 text-gray-300" />
                        <p>No se encontró stock con los filtros seleccionados</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="p-3 text-left text-sm font-semibold">Producto</th>
                                    <th className="p-3 text-left text-sm font-semibold">Sucursal</th>
                                    <th className="p-3 text-left text-sm font-semibold">Bodega</th>
                                    <th className="p-3 text-center text-sm font-semibold">Stock Actual</th>
                                    <th className="p-3 text-center text-sm font-semibold">Stock Mínimo</th>
                                    <th className="p-3 text-center text-sm font-semibold">Estado</th>
                                    <th className="p-3 text-right text-sm font-semibold">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {stock.map((item) => {
                                    const bajoMinimo = item.cantidad <= item.stock_minimo;
                                    const sinStock = item.cantidad <= 0;
                                    return (
                                        <tr key={`${item.producto_id}_${item.sucursal_id}_${item.bodega_id}`} 
                                            className={`hover:bg-gray-50 ${bajoMinimo ? 'bg-orange-50' : ''} ${sinStock ? 'bg-red-50' : ''}`}>
                                            <td className="p-3">
                                                <div className="font-medium">{item.producto_nombre}</div>
                                                <div className="text-xs text-gray-500">{item.codigo_barra}</div>
                                            </td>
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <Building2 size={14} className="text-gray-400" />
                                                    <span>{item.sucursal_nombre || 'N/A'}</span>
                                                </div>
                                            </td>
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <Warehouse size={14} className="text-gray-400" />
                                                    <span>{item.bodega_nombre || 'N/A'}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 text-center font-bold font-mono">
                                                {item.cantidad}
                                            </td>
                                            <td className="p-3 text-center text-sm text-gray-500">
                                                {item.stock_minimo || 0}
                                            </td>
                                            <td className="p-3 text-center">
                                                {sinStock ? (
                                                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-bold">
                                                        Sin Stock
                                                    </span>
                                                ) : bajoMinimo ? (
                                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-bold">
                                                        Bajo Mínimo
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">
                                                        OK
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-3">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => openUpdateModal(item)}
                                                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded"
                                                        title="Ajustar Stock"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => openTransferModal(item)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                                        title="Transferir Stock"
                                                    >
                                                        <ArrowRight size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Update Stock Modal */}
            {showUpdateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold mb-4">Ajustar Stock</h3>
                        <form onSubmit={handleUpdateStock}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Producto</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border rounded bg-gray-50"
                                        value={selectedProduct?.producto_nombre || ''}
                                        disabled
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Sucursal</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 border rounded bg-gray-50"
                                            value={selectedProduct?.sucursal_nombre || ''}
                                            disabled
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Bodega</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 border rounded bg-gray-50"
                                            value={selectedProduct?.bodega_nombre || ''}
                                            disabled
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Cantidad</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border rounded"
                                        value={updateData.cantidad}
                                        onChange={(e) => setUpdateData({ ...updateData, cantidad: parseInt(e.target.value) || 0 })}
                                        min="0"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Motivo (opcional)</label>
                                    <textarea
                                        className="w-full p-2 border rounded"
                                        rows="3"
                                        value={updateData.motivo}
                                        onChange={(e) => setUpdateData({ ...updateData, motivo: e.target.value })}
                                        placeholder="Ej: Ajuste por inventario físico"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowUpdateModal(false)}
                                    className="flex-1 btn-secondary"
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="flex-1 btn-primary">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Transfer Stock Modal */}
            {showTransferModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold mb-4">Transferir Stock</h3>
                        <form onSubmit={handleTransferStock}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Producto</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border rounded bg-gray-50"
                                        value={selectedProduct?.producto_nombre || ''}
                                        disabled
                                    />
                                </div>
                                <div className="bg-gray-50 p-3 rounded">
                                    <div className="text-sm font-semibold mb-2">Origen</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Sucursal</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 border rounded bg-white text-sm"
                                                value={selectedProduct?.sucursal_nombre || ''}
                                                disabled
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Bodega</label>
                                            <input
                                                type="text"
                                                className="w-full p-2 border rounded bg-white text-sm"
                                                value={selectedProduct?.bodega_nombre || ''}
                                                disabled
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-2 text-xs text-gray-600">
                                        Stock disponible: <span className="font-bold">{selectedProduct?.cantidad || 0}</span>
                                    </div>
                                </div>
                                <div className="bg-blue-50 p-3 rounded">
                                    <div className="text-sm font-semibold mb-2">Destino</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Sucursal</label>
                                            <select
                                                className="w-full p-2 border rounded text-sm"
                                                value={transferData.sucursal_destino}
                                                onChange={(e) => setTransferData({ ...transferData, sucursal_destino: e.target.value, bodega_destino: '' })}
                                                required
                                            >
                                                <option value="">Seleccionar...</option>
                                                {sucursales.filter(s => (s.id || s.sucursal_id) != transferData.sucursal_origen).map(s => (
                                                    <option key={s.id || s.sucursal_id} value={s.id || s.sucursal_id}>
                                                        {s.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Bodega</label>
                                            <select
                                                className="w-full p-2 border rounded text-sm"
                                                value={transferData.bodega_destino}
                                                onChange={(e) => setTransferData({ ...transferData, bodega_destino: e.target.value })}
                                                disabled={!transferData.sucursal_destino}
                                                required
                                            >
                                                <option value="">Seleccionar...</option>
                                                {bodegasDestino.map(b => (
                                                    <option key={b.id || b.bodega_id} value={b.id || b.bodega_id}>
                                                        {b.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Cantidad a Transferir</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border rounded"
                                        value={transferData.cantidad}
                                        onChange={(e) => setTransferData({ ...transferData, cantidad: parseInt(e.target.value) || 0 })}
                                        min="1"
                                        max={selectedProduct?.cantidad || 0}
                                        required
                                    />
                                    <div className="text-xs text-gray-500 mt-1">
                                        Máximo disponible: {selectedProduct?.cantidad || 0}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Motivo (opcional)</label>
                                    <textarea
                                        className="w-full p-2 border rounded"
                                        rows="2"
                                        value={transferData.motivo}
                                        onChange={(e) => setTransferData({ ...transferData, motivo: e.target.value })}
                                        placeholder="Ej: Transferencia entre sucursales"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowTransferModal(false)}
                                    className="flex-1 btn-secondary"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    className="flex-1 btn-primary"
                                    disabled={!transferData.sucursal_destino || !transferData.bodega_destino || transferData.cantidad <= 0}
                                >
                                    Transferir
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
